import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { AgTableCard, Button, Badge } from '../../UI';
import { useUserContext } from '../../../contexts/UserContext';
import { useEPMContext } from '../../../contexts/EPMContext';
import { permission } from '../../../config/permission';
import { getAllEpmTags, deleteEpmTag } from '../../../utils/APIs';
import AddTagModal from './AddTagModal';
import ConfirmModal from '../../UI/Modal/confirmModal';

const Tags = () => {
    const { userData, checkPermission } = useUserContext();
    const canCreate = checkPermission(permission.XCM_SETTINGS_TAGS_CREATE);
    const canDelete = checkPermission(permission.XCM_SETTINGS_TAGS_DELETE);
    const { tests = [], getEpmData } = useEPMContext();
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchTags = useCallback(async () => {
        const token = userData?.token;
        const orgToken = userData?.selectedOrganization?.org_token;
        if (!token || !orgToken) return;

        setLoading(true);
        try {
            const response = await getAllEpmTags(token, orgToken);
            setTags(response?.data ?? []);
        } catch (error) {
            toast.error(error?.message || 'Unable to load tags.');
        } finally {
            setLoading(false);
        }
    }, [userData?.token, userData?.selectedOrganization?.org_token]);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const rowData = useMemo(() => tags.map((tag) => {
        const usageCount = tests.filter((test) => (test?.tag_ids ?? []).some((tagId) => String(tagId?._id ?? tagId) === String(tag._id))).length;
        return { ...tag, tag: String(tag?.name ?? ''), usedOn: `${usageCount} ${usageCount === 1 ? 'test' : 'tests'}` };
    }), [tags, tests]);

    const confirmDelete = async () => {
        if (!deleteTarget?._id) return;

        setDeleteLoading(true);
        try {
            const response = await deleteEpmTag(userData?.token, userData?.selectedOrganization?.org_token, deleteTarget._id);
            toast.success(response?.message || 'Tag deleted successfully.');
            setDeleteTarget(null);
            await Promise.all([fetchTags(), getEpmData()]);
        } catch (error) {
            toast.error(error?.message || 'Unable to delete the tag.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const columnDefs = useMemo(() => [
        { headerName: 'Tag', field: 'tag', sortable: true, filter: true, flex: 2, minWidth: 180 },
        { headerName: 'Used On', field: 'usedOn', sortable: true, filter: true, flex: 1, minWidth: 140 },
        ...(canDelete ? [{
            headerName: 'Actions', field: 'actions', sortable: false, filter: false, width: 120,
            cellRenderer: ({ data }) => (
                <div className="stride-table-actions">
                    <button
                        className="stride-table-btn stride-table-btn-delete"
                        type="button"
                        aria-label={`Delete ${data.tag}`}
                        title="Delete tag"
                        onClick={() => setDeleteTarget(data)}
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
            ),
        }] : []),
    ], [canDelete]);

    const handleTagCreated = useCallback(async () => {
        await Promise.all([fetchTags(), getEpmData()]);
        setShowAddModal(false);
    }, [fetchTags, getEpmData]);

    return (
        <div className="epm-tags">
            <div className="epm-settings__section-heading">
                <div>
                    <h2 className="text-lg font-bold text-stride-heading">Tags</h2>
                    <p className="text-xs text-stride-muted mt-1">Tags help group and find related tests across XCM.</p>
                </div>
                <div className="flex items-center gap-2.5">
                    <Badge variant="primary" appearance="soft">{rowData.length} {rowData.length === 1 ? 'tag' : 'tags'}</Badge>
                    {canCreate && (
                        <Button
                            variant="primary"
                            size="sm"
                            icon={<Plus size={15} />}
                            onClick={() => setShowAddModal(true)}
                        >
                            Add New Tag
                        </Button>
                    )}
                </div>
            </div>
            <AgTableCard
                tableId="epm-settings-tags"
                theme="ag-theme-quartz"
                loading={loading}
                rowData={rowData}
                columnDefs={columnDefs}
                getRowId={({ data }) => data._id}
                pagination={true}
                paginationPageSize={10}
                emptyTitle="No tags found"
                emptyMessage="Create tags to group and organize tests across XCM."
            />
            {showAddModal && <AddTagModal onClose={() => setShowAddModal(false)} onSaved={handleTagCreated} />}
            {deleteTarget && (
                <ConfirmModal
                    title="Delete Tag"
                    description={`Are you sure you want to delete the “${deleteTarget.tag}” tag?`}
                    ConfirmText="Delete"
                    loading={deleteLoading}
                    hideModal={() => setDeleteTarget(null)}
                    handleConfirmation={confirmDelete}
                />
            )}
        </div>
    );
};

export default Tags;
